import React, { useMemo, useState } from 'react';
import Popover from './Popover';
import Input from './Input';
import { Icon } from '../icons';
import { useT } from '../../i18n';

const pad = (n) => String(n).padStart(2, '0');

function buildTimes(stepMinutes) {
  const out = [];
  for (let m = 0; m < 24 * 60; m += stepMinutes) {
    out.push(`${pad(Math.floor(m / 60))}:${pad(m % 60)}`);
  }
  return out;
}

const to12h = (hhmm) => {
  const [h, m] = hhmm.split(':').map(Number);
  const period = h < 12 ? 'AM' : 'PM';
  const hr = h % 12 === 0 ? 12 : h % 12;
  return `${hr}:${pad(m)} ${period}`;
};

/**
 * TimePicker — pick a time of day from a stepped list (with typeahead filter).
 *
 * Props:
 *   value: 'HH:mm', onChange('HH:mm')
 *   minuteStep: number (default 30)
 *   use12Hour: boolean (display only; value stays 'HH:mm')
 *   size, disabled, ariaLabel, placeholder
 */
const TimePicker = ({
  value = '',
  onChange,
  minuteStep = 30,
  use12Hour = true,
  size = 'md',
  disabled = false,
  ariaLabel,
  placeholder,
  className = '',
}) => {
  const t = useT();
  const [query, setQuery] = useState('');
  const times = useMemo(() => buildTimes(minuteStep), [minuteStep]);
  const display = (t) => (use12Hour ? to12h(t) : t);

  const filtered = query
    ? times.filter((t) => t.includes(query) || display(t).toLowerCase().includes(query.toLowerCase()))
    : times;

  const trigger = (
    <button
      type="button"
      className={`ps-select ps-select--${size} ps-timepicker__trigger ${disabled ? 'is-disabled' : ''}`.trim()}
      disabled={disabled}
      aria-label={ariaLabel || t('Select time')}
    >
      <Icon.Clock size={16} className="ps-timepicker__icon" />
      <span className={`ps-select__value ${value ? '' : 'ps-select__value--placeholder'}`.trim()}>
        {value ? display(value) : (placeholder ?? t('Select time'))}
      </span>
      <Icon.ChevronDown size={16} className="ps-select__caret" />
    </button>
  );

  return (
    <Popover trigger={trigger} role="dialog" placement="bottom-start" className={className} panelClassName="ps-popover__panel--combobox">
      {({ close }) => (
        <div className="ps-combobox">
          <div className="ps-combobox__search">
            <Input
              size="sm"
              autoFocus
              leadingIcon={<Icon.Search />}
              placeholder={t('Type a time…')}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
          <div className="ps-combobox__list" role="listbox">
            {filtered.length === 0 ? (
              <p className="ps-combobox__empty">{t('No times')}</p>
            ) : (
              filtered.map((t) => {
                const isSel = t === value;
                return (
                  <button
                    key={t}
                    type="button"
                    role="option"
                    aria-selected={isSel}
                    className={`ps-listbox__option ${isSel ? 'is-selected' : ''}`.trim()}
                    onClick={() => { onChange?.(t); setQuery(''); close(); }}
                  >
                    <span className="ps-listbox__label">{display(t)}</span>
                    {isSel && <Icon.Check size={16} className="ps-listbox__check" />}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </Popover>
  );
};

export default TimePicker;
